import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AppContext = createContext();

// Supported currencies
export const CURRENCIES = [
  { code: 'USD', symbol: '$',  name: 'US Dollar',        flag: '🇺🇸', locale: 'en-US' },
  { code: 'INR', symbol: '₹',  name: 'Indian Rupee',     flag: '🇮🇳', locale: 'en-IN' },
  { code: 'EUR', symbol: '€',  name: 'Euro',             flag: '🇪🇺', locale: 'de-DE' },
  { code: 'GBP', symbol: '£',  name: 'British Pound',    flag: '🇬🇧', locale: 'en-GB' },
  { code: 'JPY', symbol: '¥',  name: 'Japanese Yen',     flag: '🇯🇵', locale: 'ja-JP' },
  { code: 'AED', symbol: 'د.إ',name: 'UAE Dirham',       flag: '🇦🇪', locale: 'ar-AE' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar',  flag: '🇨🇦', locale: 'en-CA' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar',flag: '🇦🇺', locale: 'en-AU' },
];

const initialTransactions = [
  // April 2025
  { id: 1,  title: 'Grocery Shopping',   amount: 125.75,  category: 'Food',          type: 'expense', date: '2025-04-05' },
  { id: 2,  title: 'Freelance Work',     amount: 1200.00, category: 'Salary',        type: 'income',  date: '2025-04-03' },
  { id: 3,  title: 'Electric Bill',      amount: 78.90,   category: 'Utilities',     type: 'expense', date: '2025-04-01' },
  { id: 4,  title: 'New Laptop',         amount: 899.99,  category: 'Electronics',   type: 'expense', date: '2025-04-02' },
  { id: 5,  title: 'Restaurant Dinner',  amount: 65.50,   category: 'Food',          type: 'expense', date: '2025-04-04' },
  { id: 6,  title: 'Gym Membership',     amount: 45.00,   category: 'Health',        type: 'expense', date: '2025-04-01' },
  { id: 7,  title: 'Uber Rides',         amount: 32.75,   category: 'Transport',     type: 'expense', date: '2025-04-06' },
  // March 2025
  { id: 8,  title: 'Monthly Salary',     amount: 3200.00, category: 'Salary',        type: 'income',  date: '2025-03-30' },
  { id: 9,  title: 'Rent Payment',       amount: 1200.00, category: 'Housing',       type: 'expense', date: '2025-03-28' },
  { id: 10, title: 'Internet Bill',      amount: 64.99,   category: 'Utilities',     type: 'expense', date: '2025-03-25' },
  { id: 11, title: 'Supermarket',        amount: 187.30,  category: 'Food',          type: 'expense', date: '2025-03-20' },
  { id: 12, title: 'Movie Night',        amount: 28.50,   category: 'Entertainment', type: 'expense', date: '2025-03-18' },
  { id: 13, title: 'Dividend Income',    amount: 420.00,  category: 'Investment',    type: 'income',  date: '2025-03-15' },
  // February 2025
  { id: 14, title: 'Freelance Project',  amount: 850.00,  category: 'Salary',        type: 'income',  date: '2025-02-15' },
  { id: 15, title: 'Car Insurance',      amount: 125.00,  category: 'Transport',     type: 'expense', date: '2025-02-10' },
  { id: 16, title: 'Dentist Visit',      amount: 150.00,  category: 'Healthcare',    type: 'expense', date: '2025-02-05' },
  { id: 17, title: 'Amazon Purchase',    amount: 89.99,   category: 'Shopping',      type: 'expense', date: '2025-02-01' },
  { id: 18, title: 'Monthly Salary',     amount: 3200.00, category: 'Salary',        type: 'income',  date: '2025-02-28' },
  { id: 19, title: 'Coffee Shop',        amount: 42.00,   category: 'Food',          type: 'expense', date: '2025-02-12' },
  { id: 20, title: 'Spotify Premium',    amount: 9.99,    category: 'Entertainment', type: 'expense', date: '2025-02-01' },
  // January 2025
  { id: 21, title: 'Monthly Salary',     amount: 3200.00, category: 'Salary',        type: 'income',  date: '2025-01-31' },
  { id: 22, title: 'Rent Payment',       amount: 1200.00, category: 'Housing',       type: 'expense', date: '2025-01-28' },
  { id: 23, title: 'New Year Gift',      amount: 200.00,  category: 'Shopping',      type: 'expense', date: '2025-01-02' },
  { id: 24, title: 'Stock Dividend',     amount: 310.00,  category: 'Investment',    type: 'income',  date: '2025-01-20' },
  { id: 25, title: 'Gym Membership',     amount: 45.00,   category: 'Health',        type: 'expense', date: '2025-01-01' },
  // December 2024
  { id: 26, title: 'Monthly Salary',     amount: 3200.00, category: 'Salary',        type: 'income',  date: '2024-12-31' },
  { id: 27, title: 'Christmas Shopping', amount: 450.00,  category: 'Shopping',      type: 'expense', date: '2024-12-20' },
  { id: 28, title: 'Rent Payment',       amount: 1200.00, category: 'Housing',       type: 'expense', date: '2024-12-28' },
  { id: 29, title: 'Holiday Dinner',     amount: 120.00,  category: 'Food',          type: 'expense', date: '2024-12-25' },
  { id: 30, title: 'Bonus Income',       amount: 1500.00, category: 'Salary',        type: 'income',  date: '2024-12-15' },
];

const loadFromStorage = (key, fallback) => {
  try {
    const val = localStorage.getItem(key);
    return val !== null ? JSON.parse(val) : fallback;
  } catch {
    return fallback;
  }
};

export const AppProvider = ({ children }) => {
  const [transactions, setTransactions] = useState(() => loadFromStorage('fd_transactions', initialTransactions));
  const [role, setRole]       = useState(() => loadFromStorage('fd_role', 'admin'));
  const [darkMode, setDarkMode] = useState(() => loadFromStorage('fd_dark', true));
  const [currencyCode, setCurrencyCode] = useState(() => loadFromStorage('fd_currency', 'USD'));

  // Persist each slice
  useEffect(() => { localStorage.setItem('fd_transactions', JSON.stringify(transactions)); }, [transactions]);
  useEffect(() => { localStorage.setItem('fd_role', JSON.stringify(role)); }, [role]);
  useEffect(() => { localStorage.setItem('fd_currency', JSON.stringify(currencyCode)); }, [currencyCode]);
  useEffect(() => {
    localStorage.setItem('fd_dark', JSON.stringify(darkMode));
    document.documentElement.classList.toggle('dark', darkMode);
  }, [darkMode]);

  // Derived currency object
  const currency = CURRENCIES.find(c => c.code === currencyCode) || CURRENCIES[0];

  // Centralized formatter — all components call this instead of hardcoding $
  const formatCurrency = useCallback((amount, opts = {}) => {
    const num = parseFloat(amount) || 0;
    const { showSign = false, compact = false } = opts;

    // JPY has no decimal places by convention
    const decimals = currency.code === 'JPY' ? 0 : 2;

    let formatted;
    try {
      formatted = new Intl.NumberFormat(currency.locale, {
        style: 'currency',
        currency: currency.code,
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
        notation: compact ? 'compact' : 'standard',
      }).format(num);
    } catch {
      // Fallback for locales that may not be fully supported
      formatted = `${currency.symbol}${num.toFixed(decimals)}`;
    }

    return showSign && num > 0 ? `+${formatted}` : formatted;
  }, [currency]);

  const addTransaction = (tx) => {
    setTransactions(prev => [{ ...tx, id: Date.now(), amount: parseFloat(tx.amount) }, ...prev]);
  };

  const updateTransaction = (id, updates) => {
    setTransactions(prev => prev.map(tx =>
      tx.id === id ? { ...tx, ...updates, amount: parseFloat(updates.amount) } : tx
    ));
  };

  const deleteTransaction = (id) => {
    setTransactions(prev => prev.filter(tx => tx.id !== id));
  };

  return (
    <AppContext.Provider value={{
      transactions, addTransaction, updateTransaction, deleteTransaction,
      role, setRole,
      darkMode, setDarkMode,
      currency, currencyCode, setCurrencyCode,
      formatCurrency,
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => useContext(AppContext);
